import React, { useState, useCallback } from 'react'
import { Button, Empty, message, Space, Tabs } from 'antd'
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useCompany } from '../../../../contexts/CompanyContext'
import { useConfig } from '../../../../contexts/ConfigContext'
import { useRegister } from '../../../../contexts/RegisterContext'
import { useSecureRequest } from '../../../../hooks/useSecureRequest'
import { IMovementRS } from '../../../../models/storageModel'
import { movementStatus } from '../../../../config/const'
import MovementReportFilters from './MovementReportFilters'
import MovementsByPeriodReport, { periodExcelColumns, getPeriodExcelData } from './MovementsByPeriodReport'
import MovementsByTypeReport, { typeExcelColumns, getTypeExcelData } from './MovementsByTypeReport'
import MovementsByStorehouseReport, { storehouseExcelColumns, getStorehouseExcelData } from './MovementsByStorehouseReport'
import MovementsByContactReport, { contactExcelColumns, getContactExcelData } from './MovementsByContactReport'
import TopMovedProductsReport, { topProductsExcelColumns, getTopProductsExcelData } from './TopMovedProductsReport'
import MovementsByPaymentReport, { paymentExcelColumns, getPaymentExcelData } from './MovementsByPaymentReport'
import MovementsByOperatorReport, { operatorExcelColumns, getOperatorExcelData } from './MovementsByOperatorReport'
import TransfersBetweenStorehousesReport, { transferExcelColumns, getTransferExcelData } from './TransfersBetweenStorehousesReport'
import { exportToExcel, downloadBase64File } from '../../../../utils/excelExport'

type TMovementReportTab = 'period' | 'type' | 'storehouse' | 'contact' | 'products' | 'payment' | 'operator' | 'transfers'

export const MovementReportsSection: React.FC = () => {
  const { company } = useCompany()
  const { config } = useConfig()
  const { register } = useRegister()
  const { securePost } = useSecureRequest()

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ])
  const [movementTypeFilter, setMovementTypeFilter] = useState<string>('ALL')
  const [activeTab, setActiveTab] = useState<TMovementReportTab>('period')
  const [movements, setMovements] = useState<IMovementRS[]>([])
  const [storehouseMap, setStorehouseMap] = useState<Map<string | number, string>>(new Map())
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const fetchMovementsData = useCallback(async () => {
    if (!company.current?.uid) return

    setLoading(true)
    setMovements([])
    setHasSearched(true)

    try {
      const startDate = dateRange[0].valueOf()
      const endDate = dateRange[1].valueOf()

      // 1 Sola Petición HTTP al endpoint agregador en backend
      const res = await securePost('/inventory/reports/get-storage-report-summary', {
        company: company.current.uid,
        startDate,
        endDate,
        movementTypeFilter,
        module: config.module
      })

      if (res && res.status === 200) {
        const mapStores = new Map<string | number, string>()
        if (Array.isArray(res.storehouses)) {
          res.storehouses.forEach((s: any) => {
            if (s.id) mapStores.set(s.id, s.name || s.alias || '')
            if (s.uid) mapStores.set(s.uid, s.name || s.alias || '')
          })
        }
        setStorehouseMap(mapStores)
        setMovements(res.movements || [])

        if (!res.movements || res.movements.length === 0) {
          message.info('No se encontraron registros para el período y filtro seleccionados')
        } else {
          message.success(`Se cargaron ${res.movements.length} movimientos de almacén`)
        }
      } else {
        message.error(res?.message || 'No fue posible cargar el informe de almacén')
      }
    } catch (error) {
      console.error('Error fetching movement reports:', error)
      message.error('Ocurrió un error al generar los informes de almacén')
    } finally {
      setLoading(false)
    }
  }, [company.current?.uid, config.module, dateRange, movementTypeFilter, securePost])

  const handleExport = async () => {
    if (movements.length === 0) {
      message.warning('No hay datos para exportar')
      return
    }

    try {
      const dateLabel = `${dateRange[0].format('YYYYMMDD')}_${dateRange[1].format('YYYYMMDD')}`
      let rows: any[] = []
      let columns: any[] = []

      switch (activeTab) {
        case 'period':
          rows = getPeriodExcelData(movements, storehouseMap)
          columns = periodExcelColumns
          break
        case 'type':
          rows = getTypeExcelData(movements)
          columns = typeExcelColumns
          break
        case 'storehouse':
          rows = getStorehouseExcelData(movements, storehouseMap)
          columns = storehouseExcelColumns
          break
        case 'contact':
          rows = getContactExcelData(movements)
          columns = contactExcelColumns
          break
        case 'products':
          rows = getTopProductsExcelData(movements)
          columns = topProductsExcelColumns
          break
        case 'payment':
          rows = getPaymentExcelData(movements, register.paymentOptions)
          columns = paymentExcelColumns
          break
        case 'operator':
          rows = getOperatorExcelData(movements)
          columns = operatorExcelColumns
          break
        case 'transfers':
          rows = getTransferExcelData(movements, storehouseMap)
          columns = transferExcelColumns
          break
      }

      // Solicitud cifrada al backend para generar Excel en servidor y retornar Base64
      const res = await securePost('/inventory/reports/export-storage-excel', {
        company: company.current?.uid,
        reportType: activeTab,
        rows,
        columns,
        fileName: `Informe_Almacen_${activeTab}_${dateLabel}.xlsx`
      })

      if (res && res.status === 200 && res.fileContentBase64) {
        downloadBase64File(res.fileContentBase64, res.fileName || `Informe_Almacen_${dateLabel}.xlsx`)
        message.success('Archivo Excel cifrado descargado exitosamente desde el servidor')
      } else {
        message.error('No se pudo generar el archivo Excel en el servidor')
      }
    } catch (err) {
      console.error('Error al exportar Excel:', err)
      message.error('Error al descargar el archivo Excel cifrado')
    }
  }

  const tabItems = [
    {
      key: 'period',
      label: 'Por Período',
      children: <MovementsByPeriodReport data={movements} storehouseMap={storehouseMap} loading={loading} />
    },
    {
      key: 'type',
      label: 'Por Tipo',
      children: <MovementsByTypeReport data={movements} loading={loading} />
    },
    {
      key: 'storehouse',
      label: 'Por Almacén',
      children: <MovementsByStorehouseReport data={movements} storehouseMap={storehouseMap} loading={loading} />
    },
    {
      key: 'contact',
      label: 'Por Contacto / Proveedor',
      children: <MovementsByContactReport data={movements} loading={loading} />
    },
    {
      key: 'products',
      label: 'Productos Más Movidos',
      children: <TopMovedProductsReport data={movements} loading={loading} />
    },
    {
      key: 'payment',
      label: 'Método de Pago',
      children: <MovementsByPaymentReport data={movements} paymentOptions={register.paymentOptions} loading={loading} />
    },
    {
      key: 'operator',
      label: 'Por Operador',
      children: <MovementsByOperatorReport data={movements} loading={loading} />
    },
    {
      key: 'transfers',
      label: 'Transferencias',
      children: <TransfersBetweenStorehousesReport data={movements} storehouseMap={storehouseMap} loading={loading} />
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <MovementReportFilters
          dateRange={dateRange}
          onDateChange={setDateRange}
          movementTypeFilter={movementTypeFilter}
          onMovementTypeChange={setMovementTypeFilter}
          loading={loading}
        />
        <Space wrap>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetchMovementsData}
            loading={loading}
          >
            Generar Informe
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={movements.length === 0 || loading}
            style={{ borderColor: '#52c41a', color: movements.length > 0 ? '#52c41a' : undefined }}
          >
            Exportar Excel
          </Button>
        </Space>
      </div>

      {!hasSearched && !loading && (
        <Empty
          description="Seleccione un rango de fechas y presione 'Generar Informe' para consultar los movimientos"
          style={{ padding: '60px 0' }}
        />
      )}

      {hasSearched && movements.length === 0 && !loading && (
        <Empty
          description="No se encontraron registros para el período y filtro seleccionados"
          style={{ padding: '60px 0' }}
        />
      )}

      {movements.length > 0 && (
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as TMovementReportTab)}
          items={tabItems}
          size="large"
          type="card"
        />
      )}
    </div>
  )
}

export default MovementReportsSection
